import { useApolloClient, useLazyQuery } from '@apollo/client';
import React, { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { User } from '../../types/user/user';
import { UserContext, UserContextProps } from '../../context/UserContext';
import { SignInScreen } from './Authentication/SignInScreen';
import Main from './Main';
import VerifyEmailScreen from './Authentication/VerifyEmailScreen';
import { GetUsersResponse, GetUsersVariables, UserQueries } from '../../graphql/user.graphql';
import { Translation, TranslationLanguage } from '../../types/translation/translation';
import { TranslationContextProps, TranslationContext } from '../../context/TranslationContext';
import { translationsManager } from '../../types/translation/Translator';
import ActivateMicrosoftSubscriptionScreen from './Authentication/microsoft/ActivateMicrosoftSubscriptionScreen';
import { ENGLISH_TRANSLATION_URL } from '../../types/translation/common';
import LoadingScreen from './Common/LoadingScreen';
import { useAuthInit } from '../../util/https.util';
import { AuthenticationRequest } from '../../requests/auth.request';
import { setAccessToken } from '../../util/auth.util';

function App() {
  const navigate = useNavigate();

  const { user, loading, setUser } = useAuthInit();

  const [currentUser, setCurrentUser] = useState<User | null>(user);
  const [companyUsers, setCompanyUsers] = useState<Map<string, User>>(new Map());

  const [translations, setTranslations] = useState<Map<string, Translation>>(new Map());

  const client = useApolloClient();

  const signOut = async () => {
    setUser(null);
    setCurrentUser(null);
    localStorage.clear();
    sessionStorage.clear();
    client.clearStore();
    setAccessToken(undefined);
    await AuthenticationRequest.logout();
    navigate('/login');
  };

  useEffect(() => {
    fetch(ENGLISH_TRANSLATION_URL)
      .then(async response => {
        const translation = Translation.fromJson(TranslationLanguage.en, await response.text());
        setTranslations(new Map(translations).set(TranslationLanguage.en, translation));
        translationsManager.setTranslation(translation);
      })
      .catch(error => console.error('Error fetching translations:', error));
  }, []);

  const [getUsers] = useLazyQuery<GetUsersResponse, GetUsersVariables>(UserQueries.getForCompany);

  const loadUsers = async (companyId: string, page: number): Promise<void> => {
    const result = await getUsers({ variables: { companyId, page } });
    const getUserResponse = result.data?.users;
    if (!getUserResponse) return;
    getUserResponse.data.forEach(u => companyUsers.set(u.userId, new User(u)));
    setCompanyUsers(new Map(companyUsers));
    if (getUserResponse.hasNext) return loadUsers(companyId, ++getUserResponse.page);
  };

  const refreshCompanyUsers = async (companyId: string) => {
    setCompanyUsers(new Map());
    await loadUsers(companyId, 0);
  };

  const userContext: UserContextProps = {
    currentUser,
    setCurrentUser,
    companyUsers,
    setCompanyUsers,
    refreshCompanyUsers,
    signOut,
    locale: User.locale(currentUser),
  };

  const translationContext: TranslationContextProps = {
    translations,
    setTranslations,
  };

  if (loading) return <LoadingScreen />;

  const routes =
    currentUser || user ? (
      <>
        <Route path='/*' element={<Main />} />
      </>
    ) : (
      <>
        <Route path='/activate/microsoft' element={<ActivateMicrosoftSubscriptionScreen />} />
        <Route path='/verify' element={<VerifyEmailScreen />} />
        <Route path='/login' element={<SignInScreen />} />
        <Route path='/*' element={<Navigate to={'/login'} />} />
      </>
    );

  return (
    <div className='body'>
      <TranslationContext.Provider value={translationContext}>
        <UserContext.Provider value={userContext}>
          <Routes>{routes}</Routes>
        </UserContext.Provider>
      </TranslationContext.Provider>
    </div>
  );
}

export default App;
